import { DURATION_INPUT_DEFAULT_VALUE, DurationInput } from "@msys/ui";
import { TextField } from "@mui/material";
import type { GridRenderEditCellParams } from "@mui/x-data-grid-premium";
import React from "react";
import { useLatest } from "react-use";
import {
  durationInMinutesToTime,
  timeToDurationInMinutes,
} from "../../../utils.js";

function valueToTimeStr(value: any): string {
  return typeof value === "number"
    ? durationInMinutesToTime(value)
    : DURATION_INPUT_DEFAULT_VALUE;
}

export const DataGridDurationEditCell = ({
  id,
  field,
  value,
  api,
}: GridRenderEditCellParams) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const [currentTimeStr, setCurrentTimeStr] = React.useState(
    DURATION_INPUT_DEFAULT_VALUE
  );
  const currentTimeStrLatest = useLatest(currentTimeStr);

  const valueAsTimeStr = valueToTimeStr(value);

  // When focused and external value changes, sync into local state
  React.useEffect(() => {
    const newValueAsTimeStr = valueToTimeStr(value);
    if (isFocused && currentTimeStrLatest.current !== newValueAsTimeStr) {
      setCurrentTimeStr(newValueAsTimeStr);
    }
  }, [currentTimeStrLatest, isFocused, value]);

  const displayValue = isFocused
    ? currentTimeStr
    : typeof value === "number"
      ? `${valueAsTimeStr} h`
      : `--:-- h`;

  const commit = React.useCallback(
    (timeStr: string) => {
      const minutes = timeToDurationInMinutes(timeStr);
      api.setEditCellValue({ id, field, value: minutes });
      api.stopCellEditMode({ id, field });
    },
    [id, field, api]
  );

  return (
    <DurationInput
      value={displayValue}
      onChange={(_e, newTimeStr) => {
        setCurrentTimeStr(newTimeStr);
      }}
      enabled={isFocused}
      input={
        <TextField
          type="text"
          value={displayValue}
          size="extra-small"
          fullWidth
          variant="outlined"
          sx={{ "& fieldset": { border: "none" } }}
          autoFocus
          onFocus={() => {
            setCurrentTimeStr(valueAsTimeStr);
            setIsFocused(true);
          }}
          onBlur={() => {
            commit(currentTimeStr);
            setIsFocused(false);
          }}
          onKeyDown={e => {
            if (e.key === "Enter") commit(currentTimeStr);
            if (e.key === "Escape")
              api.stopCellEditMode({ id, field, ignoreModifications: true });
          }}
        />
      }
    />
  );
};
